/**
 * PendingMatchesFeed
 * Cross-property inline feed of pending matches with confirm/dismiss actions
 */

import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import type { HubPendingMatch } from '../../api/external-payments-hub.api';
import { formatLocalDate } from '../../utils/date';

interface PendingMatchesFeedProps {
  matches: HubPendingMatch[];
  totalCount: number;
  onConfirm: (matchId: string, transactionId: string) => Promise<void> | void;
  onDismiss: (transactionId: string) => Promise<void> | void;
}

const CONFIDENCE_STYLES: Record<string, string> = {
  high: 'text-success bg-success/10 border-success/30',
  medium: 'text-warning bg-warning/10 border-warning/30',
  low: 'text-muted-foreground bg-muted/20 border-border',
};

interface MatchRowProps {
  match: HubPendingMatch;
  onConfirm: (matchId: string, transactionId: string) => Promise<void> | void;
  onDismiss: (transactionId: string) => Promise<void> | void;
}

function MatchRow({ match, onConfirm, onDismiss }: MatchRowProps) {
  const [isConfirming, setIsConfirming] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
      Math.abs(amount)
    );

  const handleConfirm = async () => {
    setIsConfirming(true);
    setRowError(null);
    try {
      await onConfirm(match.matchId, match.transactionId);
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to confirm match');
    } finally {
      setIsConfirming(false);
    }
  };

  const handleDismiss = async () => {
    setIsDismissing(true);
    setRowError(null);
    try {
      await onDismiss(match.transactionId);
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to dismiss match');
    } finally {
      setIsDismissing(false);
    }
  };

  const isActionPending = isConfirming || isDismissing;

  return (
    <div
      className="flex flex-col gap-1 rounded-lg border border-border bg-card p-3"
      data-testid={`pending-match-row-${match.transactionId}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-foreground">
              {formatCurrency(match.amount)}
            </span>
            <span className="text-xs text-muted-foreground">{formatLocalDate(match.date, { month: 'short', day: 'numeric' })}</span>
            <span
              className={`text-xs px-1.5 py-0.5 rounded border ${
                CONFIDENCE_STYLES[match.confidence] ?? CONFIDENCE_STYLES.low
              }`}
            >
              {match.confidence}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {match.description} · {match.propertyName}
          </p>
          <p className="text-xs text-foreground mt-0.5">
            Suggested: <span className="font-medium">{match.tenantName}</span>
          </p>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2"
            onClick={handleDismiss}
            disabled={isActionPending}
            data-testid={`pending-match-dismiss-${match.transactionId}`}
            aria-label={`Dismiss match for ${match.tenantName}`}
          >
            {isDismissing ? <span className="text-xs">...</span> : <X className="h-3 w-3" />}
          </Button>
          <Button
            size="sm"
            className="h-7 px-2 bg-primary text-primary-foreground"
            onClick={handleConfirm}
            disabled={isActionPending}
            data-testid={`pending-match-confirm-${match.matchId}`}
            aria-label={`Confirm match for ${match.tenantName}`}
          >
            {isConfirming ? <span className="text-xs">...</span> : <Check className="h-3 w-3" />}
          </Button>
        </div>
      </div>
      {rowError && (
        <p className="text-xs text-destructive" data-testid={`pending-match-error-${match.transactionId}`}>
          {rowError}
        </p>
      )}
    </div>
  );
}

export function PendingMatchesFeed({
  matches,
  totalCount,
  onConfirm,
  onDismiss,
}: PendingMatchesFeedProps) {
  if (matches.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center py-8 text-center"
        data-testid="pending-matches-empty-state"
      >
        <p className="text-sm text-muted-foreground">No pending matches to review</p>
        <p className="text-xs text-muted-foreground mt-1">
          Matched payments will appear here when detected
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="pending-matches-feed">
      {matches.map((match) => (
        <MatchRow
          key={match.transactionId}
          match={match}
          onConfirm={onConfirm}
          onDismiss={onDismiss}
        />
      ))}

      {totalCount > matches.length && (
        <p className="text-xs text-center text-muted-foreground py-2">
          Showing {matches.length} of {totalCount} pending matches
        </p>
      )}
    </div>
  );
}
